import React from 'react';
import PropTypes from 'prop-types';
import withData from '@bd/bdcore-data';
import { get } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import PageHelmet from '@bd/bdcore-pagehelmet';
import AccordionToggle from '../../components/AccordionToggle';
import LightboxPhotoList from '../../components/LightBoxPhotoList';
import PageWrapper from '../../components/PageWrapper';
import { colors } from '../../styles';
import PlanHeader from './PlanHeader';
import PlanDescription from './PlanDescription';
import FloorPlans from './FloorPlans';
import VideoGalleryList from '../../components/VideoGalleryList';
import PlanMenu from './PlanMenu';
import PlanCommunitiesMap from './PlanCommunitiesMap';

const styles = css`
.DetailSection {
  position: relative;
  z-index: 1;
  .ScrollTarget {
    position: absolute;
    display: block;
    top: 0;
    @media (min-width: 992px) {
      top: -110px;
    }
  }
}
.DetailSection_heading {
  padding: 15px;
  margin: 0;
  background-image: url('/images/icon-plus.svg');
  background-position: calc(100% - 15px) center;
  background-repeat: no-repeat;
  background-size: 10px 10px;
  border-top: 1px solid #eeeeee;
  font-size: 18px;
  outline: 0 !important;
  @media (min-width: 992px) {
    font-size: 32px;
    border: 0;
    padding: 45px 0 15px 0;
    background-image: none;
    display: none;
  }
}
.DetailSection_heading-open {
  background-image: url('/images/icon-minus.svg');
  background-color: ${colors.whiteDarker};
  padding: 15px;
}
.DetailSection:first-child {
  .DetailSection_heading {
    border-top: 0;
  }
}
.DetailSection:last-child {
  .DetailSection_heading {
    border-bottom: 1px solid #eeeeee;
  }
}
.Detail_sectionHeadingInner  {
  font-size: 21px;
  color: ${colors.brand1};
  font-weight: 500;
  text-align: center;
  padding: 20px 0;
  @media(min-width: 992px) {
    font-size: 32px;
  }
}
`;

function PlanDetail({ plan, communities, activeMapPin }) {
  return (
    <PageWrapper>
      <div className={styles}>
        <PageHelmet plan={plan} />
        <PlanHeader />
        <PlanMenu />
        {
          get(plan, 'description') &&
          <div className="DetailSection">
            <div className="ScrollTarget" id="overview" />
            <AccordionToggle
              openOnId="overview"
              button={({ contentVisible }) => (
                <div className="container">
                  <h3 className={`DetailSection_heading ${contentVisible && 'DetailSection_heading-open'}`}>Plan Description</h3>
                </div>
              )}
              onClick={() => moveTo('#overview')}
            >
              <PlanDescription plan={plan} />
            </AccordionToggle>
          </div>
        }

        {
          plan.virtual_tour &&
          <div className="DetailSection">
            <div className="ScrollTarget" id="tour" />
            <AccordionToggle
              openOnId="tour"
              button={({ contentVisible }) => (
                <div className="container">
                  <h3 className={`DetailSection_heading ${contentVisible && 'DetailSection_heading-open'}`}>Virtual Tour</h3>
                </div>
              )}
              onClick={() => moveTo('#tour')}
            >
              <div className="container mb-5">
                <VideoGalleryList video={[plan.virtual_tour]} thumb={get(plan, 'virtual_tour_thumbnail')} />
              </div>
            </AccordionToggle>
          </div>
        }

        {
          get(plan, 'elevationPhotos').length > 0 &&
          <div className="DetailSection">
            <div className="ScrollTarget" id="elevations" />
            <AccordionToggle
              openOnId="elevations"
              button={({ contentVisible }) => (
                <div className="container">
                  <h3 className={`DetailSection_heading ${contentVisible && 'DetailSection_heading-open'}`}>Elevations</h3>
                </div>
              )}
              onClick={() => moveTo('#elevations')}
            >
              <div className="container mb-5">
                <h4 className="Detail_sectionHeadingInner">Elevations</h4>
                <LightboxPhotoList
                  photos={plan.elevationPhotos}
                />
              </div>
            </AccordionToggle>
          </div>
        }

        {
          get(plan, 'floorplanPhotos').length > 0 &&
          <div className="DetailSection">
            <div className="ScrollTarget" id="plans" />
            <AccordionToggle
              openOnId="plans"
              button={({ contentVisible }) => (
                <div className="container">
                  <h3 className={`DetailSection_heading ${contentVisible && 'DetailSection_heading-open'}`}>Floor Plans</h3>
                </div>
              )}
              onClick={() => moveTo('#plans')}
            >
              <h4 className="Detail_sectionHeadingInner">Floor Plans</h4>
              <FloorPlans plan={plan} />
            </AccordionToggle>
          </div>
        }

        {
          get(plan, 'photos').length > 1 &&
          <div className="DetailSection">
            <div className="ScrollTarget" id="photos" />
            <AccordionToggle
              openOnId="photos"
              button={({ contentVisible }) => (
                <div className="container">
                  <h3 className={`DetailSection_heading ${contentVisible && 'DetailSection_heading-open'}`}>Photo Gallery</h3>
                </div>
              )}
              onClick={() => moveTo('#photos')}
            >
              <div className="container mb-5">
                <h4 className="Detail_sectionHeadingInner">Photo Gallery</h4>
                <LightboxPhotoList
                  photos={plan.photos}
                />
              </div>
            </AccordionToggle>
          </div>
        }

        {
          communities.length > 0 &&
          <div className="DetailSection">
            <div className="ScrollTarget" id="availability" />
            <AccordionToggle
              openOnId="availability"
              button={({ contentVisible }) => (
                <div className="container">
                  <h3 className={`DetailSection_heading ${contentVisible && 'DetailSection_heading-open'}`}>Available In These Neighborhoods</h3>
                </div>
              )}
              onClick={() => moveTo('#availability')}
            >
              <h4 className="Detail_sectionHeadingInner">Available In These Neighborhoods</h4>
              <PlanCommunitiesMap communities={communities} activeMapPin={activeMapPin} />
            </AccordionToggle>
          </div>
        }

      </div>
    </PageWrapper>
  );
}

PlanDetail.propTypes = {
  plan: PropTypes.object.isRequired,
  communities: PropTypes.arrayOf(PropTypes.object),
  activeMapPin: PropTypes.string,
};

PlanDetail.defaultProps = {
  plan: undefined,
  communities: [],
  activeMapPin: undefined,
};

export default withData(
  {
    community: true,
    plan: true,
    builder: true,
    communities: true,
    activeMapPin: true,
  },
  {
    required: {
      plan: true,
    },
    ifMissing404: true,
  },
)(PlanDetail);



// WEBPACK FOOTER //
// ./src/pages/PlanDetail/index.js